/**
 * Düz metin / markdown / TXT yükleyici.
 *
 * PDF ve DOCX dışında, panodan kopyalanan metni veya .txt dosyalarını analiz
 * edebilmek için. Paragraflar bloklara ayrılır; satır numarası korunur.
 */

import type { DocFormat, ExtractedDocument, TextBlock } from '../types';
import { normalizeText } from '../text/normalize';
import { assessQuality } from '../text/quality';
import type { ProgressFn } from '../extract/types';

export function documentFromText(
	text: string,
	fileName = 'metin.txt',
	format: DocFormat = 'txt'
): ExtractedDocument {
	const normalized = text.replace(/\r\n?/g, '\n').trim();
	const blocks: TextBlock[] = [];
	let offsetLine = 1;

	for (const chunk of normalized.split(/\n\s*\n/)) {
		const body = chunk.trim();
		if (!body) {
			offsetLine++;
			continue;
		}
		const lineCount = body.split('\n').length;
		const norm = normalizeText(body);
		blocks.push({
			id: `txt-${blocks.length}`,
			lineStart: offsetLine,
			lineEnd: offsetLine + lineCount - 1,
			raw: body,
			text: norm.text,
			offsetMap: norm.map
		});
		offsetLine += lineCount + 1;
	}

	const joined = blocks.map((b) => b.text).join('\n\n');
	let cursor = 0;
	const blockStarts = blocks.map((b) => {
		const s = cursor;
		cursor += b.text.length + 2;
		return s;
	});

	return {
		fileName,
		format,
		fileSize: new TextEncoder().encode(text).length,
		blocks,
		text: joined,
		blockStarts,
		pageCount: 0,
		meta: {},
		quality: assessQuality({
			text: joined,
			chars: joined.length,
			pages: 0,
			emptyPages: 0,
			format: format === 'txt' ? 'docx' : format
		}),
		warnings: []
	};
}

export async function extractPlainText(
	file: File,
	onProgress: ProgressFn = () => {}
): Promise<ExtractedDocument> {
	onProgress({ stageKey: 'proc.stage.textRead', ratio: 0.4 });
	const text = await file.text();
	onProgress({ stageKey: 'proc.stage.done', ratio: 1 });
	return documentFromText(text, file.name);
}
